// Based on https://github.com/pmndrs/drei/blob/v9.88.17/src/core/AccumulativeShadows.tsx

import * as THREE from 'three'
import * as React from 'react'
import { extend, ReactThreeFiber, useFrame, useThree } from '@react-three/fiber'
import { shaderMaterial } from './shaderMaterial'
// import { ForwardRefComponent } from '../helpers/ts-utils'
import { REVISION } from 'three'
import { DiscardMaterial } from './DiscardMaterial'
import { genFragmentShaderConstMapSize } from './ShadowsShader'
import { pixelIndexToSphericalCoords, sampleCdf, sampleUniform } from '../utils/math3d'

function isLight(object) {
  return object.isLight
}

function isGeometry(object) {
  return !!object.geometry
}

// Based on https://github.com/pmndrs/drei/blob/v9.88.17/src/helpers/constants.ts
const getVersion = () => parseInt(REVISION.replace(/\D+/g, ''))
const version = getVersion();

export const accumulativeContext = React.createContext(null)

export const SHADOW_MAP_SIZE = 1024;
const FRAGMENT_SHADER = genFragmentShaderConstMapSize(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE);

const SoftShadowMaterial = /* @__PURE__ */ shaderMaterial(
  {
    color: /* @__PURE__ */ new THREE.Color(),
    blend: 2.0,
    alphaTest: 0.75,
    opacity: 0,
    map: null,
  },
  `varying vec2 vUv;
   void main() {
     gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(position, 1.);
     vUv = uv;
   }`,
   // Use better fragment shader
   FRAGMENT_SHADER
)

export const AccumulativeShadows = /* @__PURE__ */ React.forwardRef(
  (
    {
      children,
      temporal,
      frames = 40,
      limit = Infinity,
      blend = 20,
      scale = 10,
      opacity = 1,
      alphaTest = 0.75,
      color = 'black',
      colorBlend = 2,
      resolution = 1024,
      toneMapped = true,
      ...props
    },
    forwardRef
  ) => {
    extend({ SoftShadowMaterial })

    const gl = useThree((state) => state.gl)
    const scene = useThree((state) => state.scene)
    const camera = useThree((state) => state.camera)
    const invalidate = useThree((state) => state.invalidate)
    const gPlane = React.useRef(null)
    const gLights = React.useRef(null)

    const [plm] = React.useState(() => new ProgressiveLightMap(gl, scene, resolution))
    React.useLayoutEffect(() => {
      plm.configure(gPlane.current)
    }, [])

    const api = React.useMemo(
      () => ({
        lights: new Map(),
        temporal: !!temporal,
        frames: Math.max(2, frames),
        blend: Math.max(2, frames === Infinity ? blend : frames),
        count: 0,
        getMesh: () => gPlane.current,
        reset: () => {
          // Clear buffers, reset opacities, set frame count to 0
          plm.clear()
          const material = gPlane.current.material
          material.opacity = 0
          material.alphaTest = 0
          api.count = 0
        },
        update: (frames = 1) => {
          // Adapt the opacity-blend ratio to the number of frames
          const material = gPlane.current.material
          if (!api.temporal) {
            material.opacity = opacity
            material.alphaTest = alphaTest
          } else {
            material.opacity = Math.min(opacity, material.opacity + opacity / api.blend)
            material.alphaTest = Math.min(alphaTest, material.alphaTest + alphaTest / api.blend)
          }

          // Switch accumulative lights on
          gLights.current.visible = true
          // Collect scene lights and meshes
          plm.prepare()

          // Update the lightmap and the accumulative lights
          for (let i = 0; i < frames; i++) {
            api.lights.forEach((light) => light.update())
            plm.update(camera, api.blend)
          }
          // Switch lights off
          gLights.current.visible = false
          // Restore lights and meshes
          plm.finish()
        },
      }),
      [plm, camera, scene, temporal, frames, blend, opacity, alphaTest]
    )

    React.useLayoutEffect(() => {
      // Reset internals, buffers, ...
      api.reset()
      // Update lightmap
      if (!api.temporal && api.frames !== Infinity){
        api.update(api.blend)
      }
    })

    // Expose api, allow children to set itself as the main light source
    React.useImperativeHandle(forwardRef, () => api, [api])

    useFrame(() => {
      if (api.count < api.frames && api.count < limit) {
        invalidate()
        // Our change to avoid race condition - Start
        if (api.temporal || api.frames === Infinity) {
            api.update()
            api.count++
        }
        else {
            plm.clear();
            for (let i = 0; i < api.frames; i++) {
                api.update();
            }
            api.count = api.frames;
        }
        // Our change to avoid race condition - End
      }
    })

    return (
      <group {...props}>
        <group traverse={() => null} ref={gLights}>
          <accumulativeContext.Provider value={api}>{children}</accumulativeContext.Provider>
        </group>
        <mesh receiveShadow ref={gPlane} scale={scale} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry />
          <softShadowMaterial
            transparent
            depthWrite={false}
            toneMapped={toneMapped}
            color={color}
            blend={colorBlend}
            map={plm.progressiveLightMap2.texture}
          />
        </mesh>
      </group>
    )
  }
)

export const RandomizedLight = /* @__PURE__ */ React.forwardRef(
  (
    {
      castShadow = true,
      bias = 0.001,
      mapSize = 512,
      size = 5,
      near = 0.5,
      far = 500,
      frames = 1,
      position = [0, 0, 0],
      radius = 1,
      amount = 8,
      intensity = version >= 155 ? Math.PI : 1,
      ambient = 0.5,
      // Avoid light being too low
      minLightPositionY = null,
      hdriData = null,
      ...props
    },
    forwardRef
  ) => {
    const gLights = React.useRef(null)
    const length = new THREE.Vector3(...position).length()
    const parent = React.useContext(accumulativeContext)
    // Change add hdri soft shadows - Start 1
    const {hist, cdf, width:hdriWidth, height:hdriHeight, hdriYRotation} = (hdriData || {});
    // Change add hdri soft shadows - End 1

    const update = React.useCallback(() => {
      let light = undefined;
      if (gLights.current) {
        for (let l = 0; l < gLights.current.children.length; l++) {
          light = gLights.current.children[l]
          if (Math.random() > ambient) {
            // Change add hdri soft shadows - Start 2
            const useHdri = !!hdriData;
            if (useHdri) {
              if (hist?.length && cdf?.length) {
                const bin = sampleCdf(cdf);
                const index = sampleUniform(hist[bin]);
                let {phi, theta} = pixelIndexToSphericalCoords(index, hdriWidth, hdriHeight);
                theta += hdriYRotation;
                // Add some fuzziness to create a softer light
                const halfDeltaPhi = THREE.MathUtils.DEG2RAD * 1;
                const halfDeltaTheta = halfDeltaPhi;
                phi += THREE.MathUtils.randFloat(-halfDeltaPhi, halfDeltaPhi);
                theta += THREE.MathUtils.randFloat(-halfDeltaTheta, halfDeltaTheta);
                light.position.setFromSphericalCoords(length, phi, theta);
              }
            } else {
            // Change add hdri soft shadows - End 2
              light.position.set(
                position[0] + THREE.MathUtils.randFloatSpread(radius),
                position[1] + THREE.MathUtils.randFloatSpread(radius),
                position[2] + THREE.MathUtils.randFloatSpread(radius)
            )
          }
          } else {
            let lambda = Math.acos(2 * Math.random() - 1) - Math.PI / 2.0
            let phi = 2 * Math.PI * Math.random()
            light.position.set(
              Math.cos(lambda) * Math.cos(phi) * length,
              Math.abs(Math.cos(lambda) * Math.sin(phi) * length),
              Math.sin(lambda) * length
            )
          }

          // Avoid light being too low
          if (minLightPositionY || (minLightPositionY === 0)) {
            light.position.setY(Math.max(light.position.y, minLightPositionY));
          }
          
        }
      }
    }, [radius, ambient, length, minLightPositionY, ...position, hdriHeight, hdriWidth, cdf, hist, hdriYRotation])

    const api = React.useMemo(() => ({ update }), [update])
    React.useImperativeHandle(forwardRef, () => api, [api])
    React.useLayoutEffect(() => {
      const group = gLights.current
      if (parent) parent.lights?.set(group.uuid, api)
      return () => void parent?.lights?.delete(group.uuid)
    }, [parent, api])

    return (
      <group ref={gLights} {...props}>
        {Array.from({ length: amount }, (_, index) => (
          <directionalLight
            key={index}
            castShadow={castShadow}
            shadow-bias={bias}
            shadow-mapSize={[mapSize, mapSize]}
            intensity={intensity / amount}
          >
            <orthographicCamera attach="shadow-camera" args={[-size, size, size, -size, near, far]} />
          </directionalLight>
        ))}
      </group>
    )
  }
)

// Based on "Progressive Light Map Accumulator", by [zalo](https://github.com/zalo/)
class ProgressiveLightMap {

  constructor(renderer, scene, res = 1024) {
    this.renderer = renderer
    this.res = res
    this.scene = scene
    this.buffer1Active = false
    this.lights = []
    this.meshes = []
    this.object = null
    this.clearColor = new THREE.Color()
    this.clearAlpha = 0

    // Create the Progressive LightMap Texture
    const textureParams = {
      type: THREE.HalfFloatType,
      magFilter: THREE.NearestFilter,
      minFilter: THREE.NearestFilter,
    }
    this.progressiveLightMap1 = new THREE.WebGLRenderTarget(this.res, this.res, textureParams)
    this.progressiveLightMap2 = new THREE.WebGLRenderTarget(this.res, this.res, textureParams)

    // Inject some spicy new logic into a standard phong material
    this.discardMat = new DiscardMaterial()
    this.targetMat = new THREE.MeshLambertMaterial({ fog: false })
    this.previousShadowMap = { value: this.progressiveLightMap1.texture }
    this.averagingWindow = { value: 100 }
    this.targetMat.onBeforeCompile = (shader) => {
      // Vertex Shader: Set Vertex Positions to the Unwrapped UV Positions
      shader.vertexShader =
        'varying vec2 vUv;\n' +
        shader.vertexShader.slice(0, -1) +
        'vUv = uv; gl_Position = vec4((uv - 0.5) * 2.0, 1.0, 1.0); }'

      // Fragment Shader: Set Pixels to average in the Previous frame's Shadows
      const bodyStart = shader.fragmentShader.indexOf('void main() {')
      shader.fragmentShader =
        'varying vec2 vUv;\n' +
        shader.fragmentShader.slice(0, bodyStart) +
        'uniform sampler2D previousShadowMap;\n	uniform float averagingWindow;\n' +
        shader.fragmentShader.slice(bodyStart - 1, -1) +
        `\nvec3 texelOld = texture2D(previousShadowMap, vUv).rgb;
        gl_FragColor.rgb = mix(texelOld, gl_FragColor.rgb, 1.0/ averagingWindow);
      }`

      // Set the Previous Frame's Texture Buffer and Averaging Window
      shader.uniforms.previousShadowMap = this.previousShadowMap
      shader.uniforms.averagingWindow = this.averagingWindow
    }
  }

  clear() {
    this.renderer.getClearColor(this.clearColor)
    this.clearAlpha = this.renderer.getClearAlpha()
    this.renderer.setClearColor('black', 1)
    this.renderer.setRenderTarget(this.progressiveLightMap1)
    this.renderer.clear()
    this.renderer.setRenderTarget(this.progressiveLightMap2)
    this.renderer.clear()
    this.renderer.setRenderTarget(null)
    this.renderer.setClearColor(this.clearColor, this.clearAlpha)

    this.lights = []
    this.meshes = []
    this.scene.traverse((object) => {
      if (isGeometry(object)) {
        this.meshes.push({ object, material: object.material })
      } else if (isLight(object)) {
        this.lights.push({ object, intensity: object.intensity })
      }
    })
  }

  prepare() {
    this.lights.forEach((light) => (light.object.intensity = 0))
    this.meshes.forEach((mesh) => (mesh.object.material = this.discardMat))
  }

  finish() {
    this.lights.forEach((light) => (light.object.intensity = light.intensity))
    this.meshes.forEach((mesh) => (mesh.object.material = mesh.material))
  }

  configure(object) {
    this.object = object
  }

  update(camera, blendWindow = 100) {
    if (!this.object) return
    // Set each object's material to the UV Unwrapped Surface Mapping Version
    this.averagingWindow.value = blendWindow
    this.object.material = this.targetMat
    // Ping-pong two surface buffers for reading/writing
    const activeMap = this.buffer1Active ? this.progressiveLightMap1 : this.progressiveLightMap2
    const inactiveMap = this.buffer1Active ? this.progressiveLightMap2 : this.progressiveLightMap1
    // Render the object's surface maps
    const oldBg = this.scene.background
    this.scene.background = null
    this.renderer.setRenderTarget(activeMap)
    this.previousShadowMap.value = inactiveMap.texture
    this.buffer1Active = !this.buffer1Active
    this.renderer.render(this.scene, camera)
    this.renderer.setRenderTarget(null)
    this.scene.background = oldBg
  }
}